import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  inject,
  input,
} from '@angular/core';

import moment from 'moment-timezone';

import { getDefaultTimezone, getFormattedTimezone } from '@core/time-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { TimeZonePickerDropdownComponent } from './time-zone-picker-dropdown/time-zone-picker-dropdown.component';

@Component({
  selector: 'rt-time-zone-picker',
  templateUrl: 'time-zone-picker.component.html',
  styleUrls: ['./time-zone-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.short]': 'short()',
    '[class.small]': 'small()',
  },
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ConnectedOverlayDirective,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    TimeZonePickerDropdownComponent,
  ],
})
export class TimeZonePickerComponent {
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly short = input<boolean>(false);
  public readonly small = input<boolean>(false);
  public readonly timezone = input<string>(undefined!);
  public readonly emit = input<boolean>(false);
  public readonly inputVariant = input<boolean>(false);
  public readonly dropdownIconHasMargin = input<boolean>(true);

  public readonly dropdown = input<boolean>(false);
  public readonly dropdownMinWidth = input<number>(180);
  public readonly positions = input<ConnectionPositionPair[]>([]);
  @Input() public hasMargin: boolean = true;

  public zoneChange = output<string>();
  public dropdownToggle = output<boolean>();

  public timeZoneString: string;

  private currentTimeZone: string;

  public ngOnInit(): void {
    if (this.emit()) {
      this.onZoneSelect(this.timezone() || this.defaultTimezone());
    } else {
      this.setZone(this.timezone() || this.defaultTimezone());
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { timezone } = changes;

    if (timezone && !timezone.firstChange) {
      this.setZone(this.timezone());
    }
  }

  public toggle(timezoneDropdown: ConnectedOverlayDirective): void {
    if (timezoneDropdown) {
      timezoneDropdown.open = !timezoneDropdown.open;
      this.cdRef.detectChanges();
    }
  }

  public onZoneSelect(zone: string, timezoneDropdown?: any): void {
    this.toggle(timezoneDropdown);
    this.setZone(zone);
    this.zoneChange.emit(zone);
  }

  private setZone(zone: string): void {
    this.currentTimeZone = zone;
    this.setTimeZoneString();
  }

  private defaultTimezone(): string {
    return getDefaultTimezone();
  }

  private setTimeZoneString(): void {
    const date = moment.tz(this.currentTimeZone);

    this.timeZoneString = this.short()
      ? date.format('Z')
      : getFormattedTimezone(this.currentTimeZone);
    this.cdRef.detectChanges();
  }
}
